"use client";
import BFLTabs from "@repo/shared-ui/BFLTabs";
import { useState } from "react";
import useModalOutsideClick from "@repo/shared-ui/useModalOutsideClick";
import { StocksGrid } from "./StockGrid";
import { SectionTitle } from "./SectionTitle";
import { TopCategoriesClientProps } from "./types";
import BFLLink from "@repo/shared-ui/BFLLink";
import { dlElementConstants, dlEventConstants, componentNameConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import { StockItem, StocksDataCollection } from "../../types/stocks";
import { MARKET_CAP_QUERY_PARAM, MarketCapFilter } from "./clpCardConstant";
import { getAllStocksData } from "../../services/TradingMallService";

const getStockListForTab = (
  data: StocksDataCollection | undefined,
  activeTab: string,
): StockItem[] | undefined => {
  if (activeTab === "top-gainers") return data?.topgainers?.stockList;
  if (activeTab === "top-losers") return data?.toplosers?.stockList;
  if (activeTab === "52-weeks-high") return data?.weekshigh?.stockList;
  return data?.weekslow?.stockList;
};

const TopCategoriesClient = ({
  stocksData,
  defaultImage,
  tabs,
  viewAllLabel,
  stockNotFoundImgUrl,
  stockNotFoundText,
  modalTitle,
  modalBtnCta,
  successMsg,
  errorMsg,
  title,
  marketCapFilters,
  defaultMarketCap,
}: TopCategoriesClientProps) => {
  const [activeTab, setActiveTab] = useState(tabs[0]?.value || "top-gainers");
  const [index, setIndex] = useState(0);
  const [selectedMarketCap, setSelectedMarketCap] =
    useState<MarketCapFilter>(defaultMarketCap);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stocksByMarketCap, setStocksByMarketCap] = useState<
    Record<string, StocksDataCollection>
  >({ [defaultMarketCap]: stocksData });

  const { handleBackdropClick, handleModalDialogClick } = useModalOutsideClick({
    onClose: () => setIsDropdownOpen(false),
  });

  const trackMarketCapSelection = (marketCap: MarketCapFilter) => {
    const data = {
      section_title: componentNameConstants.TOP_CATEGORIES,
      component: componentNameConstants.TOP_CATEGORIES,
      event: dlEventConstants.CLICK,
      element: dlElementConstants.STOCKS_FILTER_TAB_CLICK,
      cta_text: marketCap,
      event_type: dlEventConstants.CLICK,
    };
    const cleverTapObj = {
      EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
    };
    dataLayer("", data, cleverTapObj);
  };

  const fetchMarketCapData = async (marketCap: MarketCapFilter) => {
    setIsLoading(true);
    try {
      const [topgainers, toplosers, weekshigh, weekslow] = await Promise.all([
        getAllStocksData({ exchId: "0", entity: "topgainers", mktcap: [marketCap] }),
        getAllStocksData({ exchId: "0", entity: "toplosers", mktcap: [marketCap] }),
        getAllStocksData({ exchId: "0", entity: "52weekhigh", mktcap: [marketCap] }),
        getAllStocksData({ exchId: "0", entity: "52weeklow", mktcap: [marketCap] }),
      ]);
      setStocksByMarketCap((prev) => ({
        ...prev,
        [marketCap]: { topgainers, toplosers, weekshigh, weekslow },
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarketCapSelection = (marketCap: MarketCapFilter) => {
    trackMarketCapSelection(marketCap);
    setIsDropdownOpen(false);
    if (marketCap === selectedMarketCap) return;
    setSelectedMarketCap(marketCap);
    if (!stocksByMarketCap[marketCap]) {
       fetchMarketCapData(marketCap);
    }
  };

  const handleTabChange = (newTab: string) => {
    const tabIndex = tabs.findIndex((tab) => tab.value === newTab);
    const isTabChange = newTab !== activeTab;

    setActiveTab(newTab);
    setIndex(tabIndex);

    if (isTabChange) {
      setSelectedMarketCap(defaultMarketCap);
      setIsDropdownOpen(false);
    }
    const data = {
      section_title: componentNameConstants.TOP_CATEGORIES,
      component: componentNameConstants.TOP_CATEGORIES,
      event: dlEventConstants.CLICK,
      element: dlElementConstants.TOP_CATEGORIES_TAB_CLICK,
      cta_text: newTab,
      event_type: dlEventConstants.TAB_CLICK,
      tab_position: (tabIndex + 1).toString(),
    };
    const cleverTapObj = {
      EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
      EP_TAB: newTab,
    };
    dataLayer("", data, cleverTapObj);
  };

  const handleCardClick = (stock: StockItem) => {
    const data = {
      section_title: componentNameConstants.TOP_CATEGORIES,
      component: componentNameConstants.TOP_CATEGORIES,
      event: dlEventConstants.CLICK,
      element: dlElementConstants.VIEW_ALL_CLICK,
      cta_text: activeTab,
      event_type: dlEventConstants.CARD_CLICK,
      destination_url: stock?.redirectURL,
    };

    const cleverTapObj = {
      EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
      EP_TILE_CLICKED: stock.fullName,
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
    };
    dataLayer("", data, cleverTapObj);
  };

  const activeData = stocksByMarketCap[selectedMarketCap] ?? stocksData;

  const buildViewAllHref = (link?: string): string | undefined => {
    if (!link) return link;
    const separator = link.includes("?") ? "&" : "?";
    return `${link}${separator}${MARKET_CAP_QUERY_PARAM}=${encodeURIComponent(
      selectedMarketCap,
    )}`;
  };

  const viewAllHref = buildViewAllHref(tabs[index]?.redirectLink);

  const data = {
    section_title: componentNameConstants.TOP_CATEGORIES,
    component: componentNameConstants.TOP_CATEGORIES,
    event: dlEventConstants.CLICK,
    element: dlElementConstants.VIEW_ALL_CLICK,
    cta_text: componentNameConstants.VIEW_ALL_CATEGORIES,
    event_type: dlEventConstants.BUTTON_CLICK,
    destination_url: viewAllHref,
  };

  const cleverTapObj = {
    EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
    EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
    EP_CTA: dlElementConstants.VIEW_ALL_CLICK,
    EP_TILE_CLICKED: "",
  };

  return (
    <>
      <div className="flex items-center justify-between pt-4 px-3 pb-[16px] lg:pb-[24px] ">
        <div className="flex-1">
          <SectionTitle>{title}</SectionTitle>
        </div>
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setIsDropdownOpen((open) => !open)}
            className="flex shrink-0 cursor-pointer z-4 items-center justify-between gap-2 rounded-[4px] border border-[#E5E5E5] px-[8px] py-[4px] lg:px-[12px] lg:py-[12px] text-[12px] font-[400px] text-[#1a1a1a] md:h-[48px] md:text-[16px]"
          >
            {selectedMarketCap}
            <svg
              className={`block h-[12px] w-[12px] shrink-0 text-[#1A1A1A] transition-transform lg:h-[16px] lg:w-[16px] ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
              viewBox="0 0 14 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M1 1L7 7L13 1"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isDropdownOpen && (
            <>
              <button
                type="button"
                className="fixed inset-0 z-4 bg-transparent"
                onClick={handleBackdropClick}
                aria-label="Close market cap dropdown backdrop"
              />
              <div
                role="none"
                onClick={handleModalDialogClick}
                className="absolute min-w-[124px] right-[-3px] top-[calc(100%+4px)] z-4 flex flex-col gap-[4px] rounded-[4px] border border-gray-200 bg-white px-[8px] py-[4px] lg:px-[8px] lg:py-[8px] shadow-[0px_0px_10px_0px_#00000033]"
              >
                {marketCapFilters.map((marketCap) => (
                  <button
                    key={marketCap}
                    type="button"
                    className="flex w-full text-left cursor-pointer gap-[10px] rounded-[4px] text-[12px] text-[#1a1a1a] md:hover:bg-gray-50 md:text-[14px]"
                    onClick={() => handleMarketCapSelection(marketCap)}
                  >
                    <span className="flex-1">{marketCap}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="pl-[16px]">
        <BFLTabs
          tabs={tabs}
          selectedTab={activeTab}
          onTabChange={handleTabChange}
          className="mb-[16px] border-b border-gray-300 px-0 pb-0 text-[12px] lg:text-[16px]"
          tabClassName="px-[6px] py-1 "
          gapClassName="space-x-[6px]"
          color="text-[#002953]"
          indicatorClassName="md:bottom-[0px] bottom-[0px]"
          paragraphClassName="pb-[8px] md:pb-[12px]"
          activeTabClassName="font-medium"
        />

        <StocksGrid
          key={`${activeTab}-${selectedMarketCap}`}
          isLoading={isLoading}
          stocks={getStockListForTab(activeData, activeTab) ?? []}
          defaultImage={defaultImage}
          errorMessage={stockNotFoundText}
          stockNotFoundImgUrl={stockNotFoundImgUrl}
          modalTitle={modalTitle}
          modalBtnCta={modalBtnCta}
          successMsg={successMsg}
          errorMsg={errorMsg}
          onCardClick={handleCardClick}
          cta_text={activeTab}
          section_title={"TOP_CATEGORIES"}
          nextArrowClassName="!right-0"
        />
      </div>
      <BFLLink href={viewAllHref} ctProps={JSON.stringify(cleverTapObj)} dlProps={JSON.stringify(data)}>
        <div className="pl-4  mt-[8px] max-w-fit cursor-pointer border-0 bg-transparent px-0 pb-[16px] pt-[8px] text-left transition-opacity duration-200 ease-linear">
          <button
            type="button"
            className="text-link-primary h-[18px] cursor-pointer text-[14px] font-[500]"
          >
            {tabs[index]?.viewAlltext || viewAllLabel}
          </button>
        </div>
      </BFLLink>
    </>
  );
};

export default TopCategoriesClient;
